<template>
  <div>
    <div class="mx-12" v-if="!isPdfCvLoaded">
      <v-layout wrap justify-center>
        <v-flex xs6>
          <v-progress-linear indeterminate color="primary" class="mt-12"></v-progress-linear>
        </v-flex>
      </v-layout>
    </div>
    <edit-profile-interface
      v-if="isDossierLoaded && isPdfCvLoaded"
      :isPdfCvLoaded="isPdfCvLoaded"
      :isOcrResultsLoaded="isOcrResultsLoaded"
      :dossier="dossier"
      :ocrResults="ocrResults"
      :originalCvText="originalCvText"
      :pdfUrl="pdfUrl"
      :isConsultant="isConsultant"
      :isFromToken="isFromToken"
      :dossierHasUnsavedChanges="Boolean(lastUnsavedChangeTime)"
      @updateProfileAndCreateDossier="updateProfileAndCreateDossier"
      @showVideoDialog="showVideoDialog"
    />

    <v-dialog v-model="isShowingDossierIsBeingCreated" width="40%" persistent content-class="vDialogWithoutShadow">
      <v-layout wrap mb-6>
        <v-flex xs12 text-center>
          <v-avatar :size="225" color="primary">
            <img :src="images.robot">
          </v-avatar>
        </v-flex>
      </v-layout>
      <v-card class="px-6 pb-4 pt-4 text-center primary white--text">
        <div><b>{{ $t('a') }}</b></div>
        <v-progress-linear indeterminate color="secondary" class="mt-3"></v-progress-linear>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isShowingVideoDialog" height="90%" width="80%">
      <v-card class="pa-2 text-center" v-if="isShowingVideoDialog">
        <h3 class="mb-2 mt-2">{{ $t('video1') }}</h3>
        <video
          controls
          loop
          autoplay
          width="100%"
          height="600px"
          src="https://firebasestorage.googleapis.com/v0/b/esnwebservices-prod.appspot.com/o/public%2FSe%CC%81lection%20de%20texte.mov?alt=media&token=93b34e56-a2bb-470e-b782-ae9a24fec7a0"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

import EditProfileInterface from '@/components/profile/EditProfileInterface.vue'

const functionsUrlBase = {
  'localhost': 'http://localhost:5001/esnwebservices-dev/us-central1',
  'esnwebservices-dev.firebaseapp.com': 'https://europe-west1-esnwebservices-dev.cloudfunctions.net',
  'esnwebservices-prod.firebaseapp.com': 'https://europe-west1-esnwebservices-prod.cloudfunctions.net',
  'esnwebservices.com': 'https://europe-west1-esnwebservices-prod.cloudfunctions.net'
}[window.location.hostname]

export default {
  components: {
    EditProfileInterface
  },
  props: {
    isConsultant: {
      type: Boolean,
      default: false
    },
    isFromToken: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    dossier: null,
    ocrResults: [],
    originalCvText: null,
    pdfUrl: null,
    isUpdateProfileAndCreateDossierPending: false,
    isShowingDossierIsBeingCreated: false,
    isDossierLoaded: false,
    isPdfCvLoaded: false,
    isOcrResultsLoaded: null,
    images: {
      robot: require('@/assets/app/images/artificial_intelligence_flatline2.svg')
    },
    lastUnsavedChangeTime: null,
    intervalOfRegularlyCheckIfUpdateProfileOnlyIsNeeded: null,
    isAutoSaveDossierPending: false,
    isShowingVideoDialog: false
  }),
  async mounted() {
    // this.warmupOpenaiModel()
    await this.loadDossier()
    await this.getOriginalCvText()
    await this.getPdfCvWhenReady()
    await this.getOcrResultsWhenReady()
    setTimeout(() => {
      this.regularlyCheckIfUpdateProfileOnlyIsNeeded()
    }, 2000)
  },
  beforeDestroy() {
    if (this.intervalOfRegularlyCheckIfUpdateProfileOnlyIsNeeded) {
      clearInterval(this.intervalOfRegularlyCheckIfUpdateProfileOnlyIsNeeded)
    }
  },
  computed: {
    dossierAsStr() {
      return JSON.stringify(this.dossier)
    }
  },
  methods: {
    async loadDossier() {
      let dossier;
      if (this.isFromToken) {
        dossier = await this.getDossierFromToken()
      } else {
        const dossierSnap = await this.$db.collection("dossiers").doc(this.$route.params.dossierId).get()
        if (!dossierSnap.exists) {
          if (this.isFromToken) return this.$router.push('/from-token/error')
          if (this.isConsultant) return this.$router.push('/consultant/dossiers')
          return this.$router.push('/dossiers')
        }
        dossier = dossierSnap.data()
      }
      // console.log(JSON.stringify(dossier.profile)); // eslint-disable-line
      dossier.id = this.$route.params.dossierId
      dossier.profile.experiences = dossier.profile.experiences.map(xp => {
        xp.description = this.decreaseIndents(xp.description)
        return xp
      })
      if (!('isUsingTechnicalSkillsDescription' in dossier.profile.skills)) {
        dossier.profile.skills.isUsingTechnicalSkillsDescription = false
        dossier.profile.skills.technicalSkillsDescription = ''
        dossier.profile.skills.functionalSkillsDescription = ''
      } else {
        dossier.profile.skills.technicalSkillsDescription = this.decreaseIndents(dossier.profile.skills.technicalSkillsDescription)
        dossier.profile.skills.functionalSkillsDescription = this.decreaseIndents(dossier.profile.skills.functionalSkillsDescription)
      }
      this.dossier = dossier;
      this.isDossierLoaded = true
    },
    decreaseIndents(quillStr) {
      quillStr = quillStr.replace(/(<li class="ql-indent-)\d(">)/g, function(match) {
        const startTag = '<li class="ql-indent-'
        const endTag = '">'
        const newIndent = parseInt(match.replace(startTag, '').replace(endTag, '')) - 1
        return `${startTag}${newIndent}${endTag}`;
      });
      return quillStr
    },
    setProperExperienceDescriptionIndents() {
      let experiences = JSON.parse(JSON.stringify(this.dossier.profile.experiences))
      experiences = experiences.map(xp => {
        xp.description.replace(/(<p class="ql-indent-)\d(">)/g, function() {
          return '<p>';
        })
        xp.description = xp.description.replace(/<li>/g, function() {
          return '<li class="ql-indent-0">';
        })
        return xp
      })
      experiences = experiences.map(xp => {
        const allIndentsAsStr = xp.description.match(/(<li class="ql-indent-)\d(">)/g)
        if (!allIndentsAsStr) return xp
        const lowestIndentLevel = Math.min(...allIndentsAsStr.map(match => parseInt(match.replace('<li class="ql-indent-', '').replace('">', ''))))
        xp.description = this.adjustIndents(xp.description, lowestIndentLevel)
        return xp
      })
      // If a bullet line's text is empty, remove its bullet (=replace it by a en empty normal line)
      experiences = experiences.map(xp => {
        for (let indent of [0,1,2,3,4,5,6,7,8,9]) {
          let re = new RegExp(`(<li class="ql-indent-${indent}"><br></li>)`, 'g');
          xp.description = xp.description.replace(re, '</ul><p><br></p><ul>');
          re = new RegExp(`(<li class="ql-indent-${indent}"></li>)`, 'g');
          xp.description = xp.description.replace(re, '</ul><p><br></p><ul>');
        }
        return xp
      })
      return experiences
    },
    setProperTechnicalSkillsDescriptionIndents() {
      let description = JSON.parse(JSON.stringify(this.dossier.profile.skills.technicalSkillsDescription))
      description.replace(/<li>/g, function() {
        return '<li class="ql-indent-0">';
      })
      const allIndentsAsStr = description.match(/(<li class="ql-indent-)\d(">)/g)
      if (allIndentsAsStr) {
        const lowestIndentLevel = Math.min(...allIndentsAsStr.map(match => parseInt(match.replace('<li class="ql-indent-', '').replace('">', ''))))
        description = this.adjustIndents(description, lowestIndentLevel)
      }
      return description
    },
    setProperFunctionalSkillsDescriptionIndents() {
      let description = JSON.parse(JSON.stringify(this.dossier.profile.skills.functionalSkillsDescription))
      description.replace(/<li>/g, function() {
        return '<li class="ql-indent-0">';
      })
      const allIndentsAsStr = description.match(/(<li class="ql-indent-)\d(">)/g)
      if (allIndentsAsStr) {
        const lowestIndentLevel = Math.min(...allIndentsAsStr.map(match => parseInt(match.replace('<li class="ql-indent-', '').replace('">', ''))))
        description = this.adjustIndents(description, lowestIndentLevel)
      }
      return description
    },
    adjustIndents(quillStr, lowestIndentLevel) {
      quillStr = quillStr.replace(/(<li class="ql-indent-)\d(">)/g, function(match) {
        const startTag = '<li class="ql-indent-'
        const endTag = '">'
        const newIndent = parseInt(match.replace(startTag, '').replace(endTag, '')) - lowestIndentLevel + 1
        return `${startTag}${newIndent}${endTag}`;
      });
      quillStr = quillStr.replace(/<li>/g, '<li class="ql-indent-1">')
      return quillStr
    },
    async getPdfCvWhenReady() {
      if (this.isFromToken) {
        this.fromTokenGetPdfCvWhenReady()
      } else {
        // Wait for dossier.isPdfCvReady to switch to true in firebase db
        const dossierId = this.$route.params.dossierId
        const _this = this
        let unsubcribeFirebaseDossierListener;
        unsubcribeFirebaseDossierListener = this.$db.collection('dossiers').doc(dossierId).onSnapshot(async dossierSnap => {
          const dossier = dossierSnap.data();
          if (dossier.isPdfCvReady) {
            _this.pdfUrl = await this.$storage.ref(`companies/${dossier.companyId}/users/${dossier.userId}/dossiers/${dossierId}/cv.pdf`).getDownloadURL()
            _this.isPdfCvLoaded = true
            if (unsubcribeFirebaseDossierListener) unsubcribeFirebaseDossierListener();
          }
        })
      }
    },  
    async getOcrResultsWhenReady() {
      if (this.isFromToken) {
        this.fromTokenGetOcrResultsWhenReady()
      } else {
        // Wait for dossier.isOcrResultsDone to switch to true in firebase db
        const dossierId = this.$route.params.dossierId
        const _this = this
        let unsubcribeFirebaseDossierListener;
        unsubcribeFirebaseDossierListener = this.$db.collection('dossiers').doc(this.$route.params.dossierId).onSnapshot(async dossierSnap => {
          const dossier = dossierSnap.data();
          if (dossier.isOcrResultsDone) {
            const fileRef = _this.$storage.ref(`companies/${_this.dossier.companyId}/users/${_this.dossier.userId}/dossiers/${dossierId}/ocrResults.json`);
            const url = await fileRef.getDownloadURL()
            _this.ocrResults = (await axios.get(url)).data
            _this.isOcrResultsLoaded = true
            if (unsubcribeFirebaseDossierListener) unsubcribeFirebaseDossierListener();
          }
        })
      }
    },
    async updateProfileAndCreateDossier(data) {
      const isOnlyToUpdateProfile = data.isOnlyToUpdateProfile
      if (!isOnlyToUpdateProfile) this.isShowingDossierIsBeingCreated = true
      this.isUpdateProfileAndCreateDossierPending = true
      let profile = JSON.parse(JSON.stringify(this.dossier.profile))
      profile.experiences = this.setProperExperienceDescriptionIndents()
      profile.skills.technicalSkillsDescription = this.setProperTechnicalSkillsDescriptionIndents()
      profile.skills.functionalSkillsDescription = this.setProperFunctionalSkillsDescriptionIndents()
      if (this.isFromToken) { 
        await this.fromTokenUpdateProfileAndCreateDossier(isOnlyToUpdateProfile)
      } else {
        await this.$functions.httpsCallable('updateProfileAndCreateDossier')({
          dossierId: this.dossier.id,
          profile: profile,
          templateId: this.dossier.templateId,
          labelIds: this.dossier.labelIds,
          isRequestMadeByConsultant: this.isConsultant,
          isOnlyToUpdateProfile
        })
      }
      if (!isOnlyToUpdateProfile) {
        if (this.isFromToken) return this.$router.push('/from-token/edit-profile-success')
        if (this.isConsultant) return this.$router.push('/consultant/dossiers')
        // Normal user case :
        let queryParams = {}
        if (this.$route.query.showAllDossiersOfTheCompanyAfterCreateDossier === '1') queryParams.isShowingAllDossiersOfTheCompany = '1'
        if (this.$route.query.showOnlyThisDossierAfterCreateDossier === '1') queryParams.dossierId = this.dossier.id
        this.$router.push({ path: '/dossiers', query: queryParams })
      } else {
        this.isUpdateProfileAndCreateDossierPending = false
      }
    },
    async getDossierFromToken() {
      try {
        const url = `${functionsUrlBase}/getDossierFromToken?dossierId=${this.$route.params.dossierId}&accessToken=${this.$route.query.accessToken}`
        const response = await axios.get(url)
        const dossier = response.data.dossier
        return dossier
      } catch {
        return this.$router.push('/from-token/error')
      }
    },
    async getPdfCvDownloadUrlFromToken() {
      return new Promise(async resolve => { // eslint-disable-line
        const response = await axios.get(`${functionsUrlBase}/getPdfCvDownloadUrlFromToken?dossierId=${this.dossier.id}&accessToken=${this.$route.query.accessToken}`)
        const isPdfCvReady = response.data.isPdfCvReady
        if (!isPdfCvReady) {
          setTimeout(async () => {
            const downloadUrl = await this.getPdfCvDownloadUrlFromToken()
            return resolve(downloadUrl)
          }, 1000)
        } else {
          return resolve(response.data.downloadUrl)
        }
      })
    },
    async fromTokenGetPdfCvWhenReady() {
      const downloadUrl = await this.getPdfCvDownloadUrlFromToken()
      this.pdfUrl = downloadUrl
      this.isPdfCvLoaded = true
    },
    async getOcrResultsDownloadUrlFromToken() {
      return new Promise(async resolve => { // eslint-disable-line
        const response = await axios.get(`${functionsUrlBase}/getOcrResultsDownloadUrlFromToken?dossierId=${this.dossier.id}&accessToken=${this.$route.query.accessToken}`)
        const isOcrResultsDone = response.data.isOcrResultsDone
        if (!isOcrResultsDone) {
          setTimeout(async () => {
            const downloadUrl = await this.getOcrResultsDownloadUrlFromToken()
            return resolve(downloadUrl)
          }, 1000)
        } else {
          return resolve(response.data.downloadUrl)
        }
      })
    },
    async fromTokenGetOcrResultsWhenReady() {
      const downloadUrl = await this.getOcrResultsDownloadUrlFromToken()
      this.ocrResults = (await axios.get(downloadUrl)).data
      this.isOcrResultsLoaded = true
    },
    async fromTokenUpdateProfileAndCreateDossier(isOnlyToUpdateProfile) {
      const url = `${functionsUrlBase}/updateProfileAndCreateDossierFromToken?dossierId=${this.dossier.id}&accessToken=${this.$route.query.accessToken}`
      await axios.post(url, {
        profile: this.dossier.profile,
        templateId: this.dossier.templateId,
        labelIds: this.dossier.labelIds,
        isRequestMadeByConsultant: false,
        isOnlyToUpdateProfile
      })
    },
    regularlyCheckIfUpdateProfileOnlyIsNeeded() {
      const saveAfterXSecondsOfInactivity = 2;
      this.intervalOfRegularlyCheckIfUpdateProfileOnlyIsNeeded = setInterval(async () => {
        const isUserOnViewProfileForm = Boolean(document.getElementById('view-profile-form')) // not editing a field
        if (isUserOnViewProfileForm && !this.isUpdateProfileAndCreateDossierPending && this.lastUnsavedChangeTime && this.lastUnsavedChangeTime + saveAfterXSecondsOfInactivity * 1000 < (new Date()).getTime()) {
          this.isAutoSaveDossierPending = true
          this.lastUnsavedChangeTime = null
          await this.updateProfileAndCreateDossier({ isOnlyToUpdateProfile: true })
          this.isAutoSaveDossierPending = false
        }
      }, 1000)
    },
    async getOriginalCvText() {
      if (this.isFromToken) {
        const response = await axios.get(`${functionsUrlBase}/getOriginalCvTextFromToken?dossierId=${this.dossier.id}&accessToken=${this.$route.query.accessToken}`)
        this.originalCvText = response.data.originalCvText
      } else {
        try {
          const fileRef = this.$storage.ref(`companies/${this.dossier.companyId}/users/${this.dossier.userId}/dossiers/${this.dossier.id}/rawTextkernelProfile.json`);
          const url = await fileRef.getDownloadURL()
          this.originalCvText = (await axios.get(url)).data.DocumentText[0]
        } catch {
          this.originalCvText = ''
        }
      }
    },
    // warmupOpenaiModel() {
    //   const entireExperienceQuillStr = `<p>Avril 2020 - Janvier 2021</p><p>EDF</p><p>Stage</p><p>Développement informatique.</p>`
    //   if (this.isFromToken) {
    //     axios.post(`${functionsUrlBase}/parseEntireExperienceFromToken?dossierId=${this.$route.params.dossierId}&accessToken=${this.$route.query.accessToken}`, {
    //       entireExperienceQuillStr,
    //       isForWarmup: true
    //     })
    //   } else {
    //     this.$functions.httpsCallable('parseEntireExperience')({
    //       entireExperienceQuillStr,
    //       dossierId: this.$route.params.dossierId,
    //       isForWarmup: true
    //     })
    //   }
    // },
    showVideoDialog() {
      this.isShowingVideoDialog = true
    }
  },
  watch: {
    dossierAsStr: {
      handler(val, oldVal) {
        if (oldVal !== 'null') {
          this.lastUnsavedChangeTime = (new Date()).getTime();
        }
      },
    }
  }
}
</script>

<i18n>
{
  "fr": {
    "a": "Création du dossier en cours...",
    "video1": "Comment utiliser notre outil de sélection rapide"
  },
  "en": {
    "a": "Your CV is being created...",
    "video1": "How to use our quick selection tool"
  }
}
</i18n>
